import { GenericDrawer, Image3D, Text } from '@capsicum/common-web';
import {
	type FanTokenOfferingHowToProps,
	useFanTokenOfferingHowToDrawer
} from '@capsicum/features/fan-token-offering/shared';
import { useCallback } from 'react';
import styled from 'styled-components';

export const FanTokenOfferingHowToDrawer = ({
	closeModal,
	fanTokenOfferingId,
	onClick,
	shortCode,
	testID
}: FanTokenOfferingHowToProps) => {
	const {
		buttonText,
		fanTokenOfferingTermsLinkUK,
		footerText,
		ftoWaveInfo,
		howToDrawerTexts,
		markAsSeenForId,
		secondaryActionButtonText,
		showWaveInfo
	} = useFanTokenOfferingHowToDrawer();

	const onSecondaryActionOnButtonPress = useCallback(() => {
		window.open(fanTokenOfferingTermsLinkUK, '_blank');
	}, [fanTokenOfferingTermsLinkUK]);

	return (
		<GenericDrawer
			buttonText={buttonText}
			isButtonFullWidth
			isButtonOnRow={false}
			secondaryActionButtonText={secondaryActionButtonText}
			testID={`${testID}-howToDrawer`}
			onButtonPress={() => {
				closeModal();
				markAsSeenForId(fanTokenOfferingId);
				onClick?.();
			}}
			onSecondaryActionOnButtonPress={onSecondaryActionOnButtonPress}
		>
			<S.DrawerInfo>
				<Image3D image="ftBuyConfirmation" testID={`${testID}-howToDrawer-image`} />
				<S.Title variant="text3">{howToDrawerTexts.modalTitle}</S.Title>
				<S.Info variant="text3">{howToDrawerTexts.modalInfo}</S.Info>
				{showWaveInfo && (
					<S.Description>
						<S.DescriptionTitle variant="text3">{howToDrawerTexts.waveTableTitle}</S.DescriptionTitle>
						<S.Table>
							{ftoWaveInfo.map(
								(wave, index) =>
									wave && (
										<S.Row $wave={wave.amount} key={index}>
											<S.CellLeft variant="text3">
												{`${howToDrawerTexts.waveTableWave} ${index + 1}`}
											</S.CellLeft>
											<S.CellRight variant="text3">
												{wave.amount
													? `${wave.amount} $${shortCode}`
													: howToDrawerTexts.waveTableNoLimit}
											</S.CellRight>
										</S.Row>
									)
							)}
						</S.Table>
					</S.Description>
				)}
				<S.Footer variant="text3">{footerText}</S.Footer>
			</S.DrawerInfo>
		</GenericDrawer>
	);
};

namespace S {
	export const DrawerInfo = styled.div`
		margin-top: ${({ theme }) => theme.spacings.small};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Title = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.small};
		text-align: center;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Info = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.small};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
	`;

	export const Description = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const DescriptionTitle = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
	`;

	export const Wave = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
		display: list-item;
		list-style-type: disc;
	`;

	export const Footer = styled(Text)`
		${({ theme }) => theme.fonts.text.text3};
		text-align: center;
		white-space: pre-line;
	`;

	export const Table = styled.div`
		display: flex;
		flex-direction: column;
		border: 1px solid ${({ theme }) => theme.colors.borders.main.subtle};
		${({ theme }) => theme.border.radius.small};
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Row = styled.div<{ $wave: null | string }>`
		display: flex;
		flex-direction: row;
		border-bottom: ${({ $wave, theme }) => $wave && `1px solid ${theme.colors.borders.main.subtle}`};
	`;

	export const CellLeft = styled(Text)`
		flex: 1;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
		padding: ${({ theme }) => `${theme.spacings.xxxsmall} 0`};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
	`;

	export const CellRight = styled(Text)`
		flex: 1;
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		text-align: center;
		padding: ${({ theme }) => `${theme.spacings.xxxsmall} 0`};
	`;
}
